import {
    FilterAltOffOutlined,
    FilterAltOutlined,
    ReceiptOutlined,
} from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    CircularProgress,
    Grid,
    Tab,
    Tabs,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useContext, useEffect, useState } from "react";
import { AppContext } from "../../Utilities/AppContext";
import { DELETE, GET } from "../../Utilities/BaseService";
import { MultiPermissionAuthorize } from "../../Utilities/MultiPermissionAuthorize";
import UpdatedSearchableInput from "../../Utilities/UpdatedSearchableInput";
import { numberFormatter } from "../Reading/Bill/Cells/Last12Summary";
import DashboardCard from "./Components/DashboardCard";
import SearchableInput from "./Components/SearchableInput";
import { MeterCountReport } from "./MeterCountReport/MeterCountReport";
import { SimpleDashboard } from "./SimpleDashboard/SimpleDashboard";

type Filter = {
    district?: any;
    village?: number[];
    hamlet?: number[];
    powerstation?: any;
    startDate?: string;
    endDate?: string;
    apply?: any;
    month?: any;
    year?: any;
};

const targetFilter = (filter: Filter) =>
    Object.fromEntries(
        Object.entries(filter).filter(
            ([k, v]) => v && v !== ("all" as any) && v.length > 0
        )
    );

const getPaymentReport = (filter: any) => {
    return GET(
        "/reports/dashboard/collection-reading-amounts",
        targetFilter(filter)
    );
};

const getBillReport = (filter: any) => {
    return GET("/reports/dashboard/no-of-bills", targetFilter(filter));
};

const getUnitReport = (filter: any) => {
    return GET("/reports/dashboard/no-of-units", targetFilter(filter));
};
const initialFeedback = {
    loading: false,
    show: true,
    severity: "success",
    message: "",
};

const Dashboard = () => {
    const [filter, setFilter] = useState<Filter>({});
    const [clearFilters, setClearFilters] = useState(false);
    const [feedback, setFeedback] = useState(initialFeedback);
    const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM

    const [year, month] = filter.month?.split("-") ?? ["", ""];

    const [paymentFilter, setPaymentFilter] = useState({
        connectionType: [],
    });

    // const handleFilterClear = () => {
    //     setClearFilters(!clearFilters);
    //     setFilter({
    //         district: [],
    //         village: [],
    //         hamlet: [],
    //         powerstation: [],
    //         startDate: "",
    //         endDate: "",
    //         apply: 0,
    //     });
    //     setPaymentFilter({
    //         connectionType: [],
    //     });
    // };

    const handleFilterClear = () => {
        // Toggle clearFilters to trigger reset in children
        setClearFilters((prev) => !prev);
        // Reset all filter state
        setFilter({
            district: [],
            village: [],
            hamlet: [],
            powerstation: [],
            startDate: "",
            endDate: "",
            apply: 0,
            month: "",
            year: "",
        });

        // Reset payment filter state
        setPaymentFilter({ connectionType: [] });
    };
    const [paymentReport, setPaymentReport] = useState<any>({});
    const [dashboardVariant, setDashboardVariant] = useState<
        "graph" | "simple" | "none"
    >("graph");
    const theme = useTheme();
    const { user } = useContext(AppContext);

    const { data: _paymentReport, isFetching: isPaymentFetching } = useQuery(
        ["Payment Report", filter.apply],
        () =>
            getPaymentReport({
                ...filter,
                ...paymentFilter,
                year: year,
                month: month,
            }),
        {
            onSuccess: (res) => {
                console.log(res);
                setPaymentReport(res.data.paymentReport);
            },
        }
    );

    const { data: billReport, isFetching: isBillFetching } = useQuery(
        ["Bill Report", filter.apply],
        () =>
            getBillReport({
                ...filter,
                ...paymentFilter,
                year: year,
                month: month,
            })
    );

    const { data: unitReport, isFetching: isUnitsFetching } = useQuery(
        ["Unit Report", filter.apply],
        () =>
            getUnitReport({
                ...filter,
                ...paymentFilter,
                year: year,
                month: month,
            })
    );

    const handleClearCache = async () => {
        setFeedback({ ...initialFeedback, loading: true });
        const res = await DELETE("/cache/clear");

        if (res.status == 200) {
            setFeedback({
                loading: false,
                message: res.data.message,
                show: false,
                severity: "success",
            });

            setTimeout(() => {
                setFeedback(initialFeedback);
            }, 3000);
        } else {
            setFeedback({
                loading: true,
                message: "cache clearing failed",
                show: false,
                severity: "error",
            });
        }
    };

    useEffect(() => {
        if (user?.id) {
            const dashbaordOps: string[] = user.ops.filter((op: string) =>
                ["READ DASHBOARD", "READ SIMPLE_DASHBOARD"].includes(op)
            );

            dashbaordOps.length == 1 &&
            dashbaordOps[0] === "READ SIMPLE_DASHBOARD"
                ? setDashboardVariant("simple")
                : setDashboardVariant("graph");
        }
    }, [user]);

    useEffect(() => {
        if (filter?.district?.length == 0) {
            setFilter({ ...filter, apply: 0 });
        }
    }, [filter?.district]);

    return (
        <Box>
            <Tabs
                value={dashboardVariant}
                indicatorColor="secondary"
                textColor="secondary"
                onChange={(e, v) => setDashboardVariant(v)}
            >
                {user?.ops?.includes("READ DASHBOARD") ? (
                    <Tab label="Graph Dashboard" value="graph" />
                ) : null}

                {user?.ops?.includes("READ SIMPLE_DASHBOARD") && (
                    <Tab label="Simple Dashboard" value="simple" />
                )}
            </Tabs>

            <Card
                elevation={0}
                sx={{
                    bgcolor: theme.palette.common.white,
                    p: 2,
                    mb: "1rem",
                }}
            >
                <Grid container spacing={2}>
                    <MultiPermissionAuthorize ops={["READ DISTRICT"]}>
                        <Grid item xs={12} md={6} lg={4}>
                            <UpdatedSearchableInput
                                label="District"
                                api="/region/district"
                                filter={filter}
                                setFilter={setFilter}
                                multiple={true}
                                clearAll={clearFilters}
                                key={"District"}
                            />
                        </Grid>
                    </MultiPermissionAuthorize>

                    <MultiPermissionAuthorize ops={["READ POWERSTATION"]}>
                        <Grid item xs={12} md={6} lg={4}>
                            <SearchableInput
                                label="Powerstation"
                                api="/powerstation"
                                filter={filter}
                                setFilter={setFilter}
                                dep={{ district: filter.district }}
                                multiple={true}
                                clearAll={clearFilters}
                            />
                        </Grid>
                    </MultiPermissionAuthorize>

                    <MultiPermissionAuthorize ops={["READ VILLAGE"]}>
                        <Grid item xs={12} md={6} lg={4}>
                            <UpdatedSearchableInput
                                label="Village"
                                api="/region/village"
                                dep={[filter.district, filter.powerstation]}
                                params={{
                                    district:
                                        Array.isArray(filter.district) &&
                                        filter.district.length
                                            ? filter.district
                                            : 0,
                                    powerstation:
                                        Array.isArray(filter.powerstation) &&
                                        filter.powerstation.length
                                            ? filter.powerstation
                                            : 0,
                                }}
                                filter={filter}
                                setFilter={setFilter}
                                multiple
                                clearAll={clearFilters}
                            />
                        </Grid>
                    </MultiPermissionAuthorize>

                    <MultiPermissionAuthorize ops={["READ HAMLET"]}>
                        <Grid item xs={12} md={6} lg={4}>
                            <UpdatedSearchableInput
                                label="Hamlet"
                                api="/region/hamlet"
                                filter={filter}
                                setFilter={setFilter}
                                dep={filter.village}
                                params={{
                                    village:
                                        Array.isArray(filter.village) &&
                                        filter.village.length
                                            ? filter.village
                                            : 0,
                                    powerstation:
                                        Array.isArray(filter.powerstation) &&
                                        filter.powerstation.length
                                            ? filter.powerstation
                                            : 0,
                                }}
                                multiple={true}
                                clearAll={clearFilters}
                            />
                        </Grid>
                    </MultiPermissionAuthorize>

                    <Grid item xs={12} md={6} lg={4}>
                        <TextField
                            size="small"
                            name="month"
                            label="Month"
                            type="month"
                            fullWidth
                            value={filter.month}
                            onChange={(e) =>
                                setFilter((f) => ({
                                    ...f,
                                    month: e.target.value,
                                }))
                            }
                            InputLabelProps={{ shrink: true }}
                            inputProps={{
                                // max: currentMonth, // ✅ prevents forward months
                                // min: "2025-10",
                            }}
                            variant="outlined"
                        />
                    </Grid>

                    <Grid item xs={12} md={6} lg={4}>
                        <TextField
                            type="date"
                            size="small"
                            fullWidth
                            label="From Date"
                            InputLabelProps={{ shrink: true }}
                            onChange={(e) =>
                                setFilter({
                                    ...filter,
                                    startDate: e.target.value,
                                })
                            }
                        />
                    </Grid>

                    <Grid item xs={12} md={6} lg={4}>
                        <TextField
                            type="date"
                            size="small"
                            fullWidth
                            label="To Date"
                            InputLabelProps={{ shrink: true }}
                            onChange={(e) =>
                                setFilter({
                                    ...filter,
                                    endDate: e.target.value,
                                })
                            }
                        />
                    </Grid>

                    <MultiPermissionAuthorize ops={["READ CONNECTION_TYPE"]}>
                        <Grid item xs={12} md={6} lg={4}>
                            <UpdatedSearchableInput
                                label="Connection Types"
                                _name="connectionType"
                                api="/connection"
                                filter={paymentFilter}
                                setFilter={setPaymentFilter}
                                multiple={true}
                                clearAll={clearFilters}
                            />
                        </Grid>
                    </MultiPermissionAuthorize>

                    <Grid item xs={12} md={6} lg={4}>
                        <Button
                            color="secondary"
                            variant="contained"
                            onClick={() =>
                                setFilter({
                                    ...filter,
                                    apply: parseInt(filter.apply || 0) + 1,
                                })
                            }
                            sx={{ height: "100%" }}
                            startIcon={<FilterAltOutlined />}
                            fullWidth
                            disabled={filter?.district?.length == 0}
                        >
                            Search
                        </Button>
                    </Grid>

                    <Grid item xs={12} md={6} lg={4}>
                        <Button
                            color="error"
                            onClick={handleFilterClear}
                            sx={{ height: "100%" }}
                            startIcon={<FilterAltOffOutlined />}
                            fullWidth
                            variant="outlined"
                        >
                            Clear All{" "}
                        </Button>
                    </Grid>
                </Grid>
            </Card>

            {/* <Tooltip title={"Clear Cache"}>
                <IconButton onClick={handleClearCache}>
                    <BorderClearIcon />
                </IconButton>
            </Tooltip> */}

            <section hidden={feedback.show} style={{ paddingBottom: "1rem" }}>
                <Alert severity={feedback.severity as any}>
                    {feedback.message}
                </Alert>
            </section>

            {dashboardVariant === "graph" ? (
                <MultiPermissionAuthorize ops={["READ DASHBOARD"]}>
                    <Box mt={2}>
                        {filter?.apply > 0 && !!filter?.district?.length ? (
                            <Grid container spacing={2}>
                                <DashboardCard
                                    isFetching={isPaymentFetching}
                                    title="Payment Report"
                                    data={{
                                        chartData: {
                                            series: [
                                                parseFloat(
                                                    paymentReport?.lineRent
                                                ),
                                                parseFloat(
                                                    paymentReport.additionalCharges
                                                ) ?? 0,
                                                parseFloat(
                                                    paymentReport.connectionCharges
                                                ),
                                                paymentReport.totalAmount -
                                                    (parseFloat(
                                                        paymentReport.lineRent
                                                    ) +
                                                        (paymentReport.connectionCharges +
                                                            paymentReport.additionalCharges) ||
                                                        0),
                                            ],
                                            labels: [
                                                "Line Rent",
                                                "Additional Charges",
                                                "Connection Charges",
                                                "Units Amount",
                                            ],
                                        },
                                        cardData: {
                                            total:
                                                paymentReport.totalAmount +
                                                paymentReport.applicableSurcharge +
                                                paymentReport.additionalCharges +
                                                paymentReport.connectionCharges,

                                            collected: Math.min(
                                                paymentReport.collection_amount +
                                                    paymentReport.collection_adjustment +
                                                    paymentReport.paidSurcharge +
                                                    paymentReport.pcCollected,
                                                paymentReport.totalAmount +
                                                    paymentReport.applicableSurcharge +
                                                    paymentReport.additionalCharges +
                                                    paymentReport.connectionCharges
                                            ),

                                            arrears: Math.max(
                                                0,
                                                Math.min(
                                                    paymentReport.totalAmount +
                                                        paymentReport.applicableSurcharge +
                                                        paymentReport.additionalCharges +
                                                        paymentReport.connectionCharges -
                                                        (paymentReport.collection_amount +
                                                            paymentReport.collection_adjustment +
                                                            paymentReport.paidSurcharge +
                                                            paymentReport.pcCollected),
                                                    paymentReport.totalAmount +
                                                        paymentReport.applicableSurcharge +
                                                        paymentReport.additionalCharges +
                                                        paymentReport.connectionCharges
                                                )
                                            ),
                                        },
                                    }}
                                />

                                <Grid item xs={6}>
                                    <Card variant="outlined">
                                        <CardHeader
                                            subheader={
                                                <Box
                                                    display="flex"
                                                    gap={theme.spacing(1)}
                                                    alignItems="center"
                                                >
                                                    <ReceiptOutlined color="primary" />
                                                    <Typography>
                                                        No. of Bills
                                                    </Typography>
                                                </Box>
                                            }
                                        />

                                        <CardContent
                                            sx={{
                                                display: "flex",
                                                gap: theme.spacing(2),
                                            }}
                                        >
                                            <Typography
                                                variant="h6"
                                                color="primary"
                                            >
                                                Total Count :
                                            </Typography>

                                            <Typography variant="h6">
                                                {!isBillFetching ? (
                                                    numberFormatter.format(
                                                        billReport?.data
                                                            .noOfBills
                                                    )
                                                ) : (
                                                    <CircularProgress
                                                        size={15}
                                                    />
                                                )}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={6}>
                                    <Card variant="outlined">
                                        <CardHeader
                                            subheader={
                                                <Box
                                                    display="flex"
                                                    gap={theme.spacing(1)}
                                                    alignItems="center"
                                                >
                                                    <ReceiptOutlined color="primary" />
                                                    <Typography>
                                                        No. of Units
                                                    </Typography>
                                                </Box>
                                            }
                                        />

                                        <CardContent
                                            sx={{
                                                display: "flex",
                                                gap: theme.spacing(2),
                                            }}
                                        >
                                            <Typography
                                                variant="h6"
                                                color="primary"
                                            >
                                                Total Count :
                                            </Typography>

                                            <Typography variant="h6">
                                                {!isUnitsFetching ? (
                                                    numberFormatter.format(
                                                        unitReport?.data
                                                            .noOfBills
                                                    )
                                                ) : (
                                                    <CircularProgress
                                                        size={15}
                                                    />
                                                )}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12}>
                                    <MeterCountReport
                                        filter={filter}
                                        paymentFilter={paymentFilter}
                                    />
                                </Grid>
                            </Grid>
                        ) : (
                            <Alert severity="info">
                                Please select a district in order to see
                                dashboard figures
                            </Alert>
                        )}
                    </Box>
                </MultiPermissionAuthorize>
            ) : filter?.apply > 0 && !!filter?.district?.length ? (
                <MultiPermissionAuthorize ops={["READ SIMPLE_DASHBOARD"]}>
                    <Box mt={2}>
                        <SimpleDashboard
                            filter={filter}
                            setFilter={setFilter}
                            paymentFilter={paymentFilter}
                        />
                    </Box>
                </MultiPermissionAuthorize>
            ) : (
                <Alert severity="info" sx={{ margin: "1rem" }}>
                    Please select a district in order to see dashboard figures.
                </Alert>
            )}
        </Box>
    );
};

export default Dashboard;
